import React, { useEffect, useRef, useState } from 'react';
import { CiPause1 } from "react-icons/ci";
import { IoPlayOutline, IoPauseOutline, IoPlaySkipForwardOutline } from "react-icons/io5";

// Components
import Button from '../../../../../../components/CustomButton';
import Typography from '../../../../../../components/Typography';
import VolumeControl from '../../../../../../components/VolumeControl';
import SkipSection from '../../../../../../components/Svgs/SkipSection';
import Progress from './Progress';

// Styles
import styles from './style.module.scss';
import { PodcastSection } from '../../../../../../types';

interface AudioTrackerProps {
  audioUrl: string;
  sections: PodcastSection[];
}

const AudioTracker: React.FC<AudioTrackerProps> = ({ audioUrl, sections }) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(0.5);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);

  // Refs
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Handlers
  const handlePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        audioRef.current.volume = volume;
        setIsPlaying(true);
      }
    }
  }

  const handleTimeUpdate = (e: React.ChangeEvent<HTMLAudioElement>) => setCurrentTime(e.target.currentTime);
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  }

  const handleSkipSection = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.currentTime;

      const nextSection = sections.find((section: PodcastSection) => section.start > currentTime);
      if (nextSection) {
        audioRef.current.currentTime = nextSection.start;
      }
    }
  }

  const handleSeek = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  }

  const formatCurrentTime = () => {
    const minutes = Math.floor(currentTime / 60);
    const seconds = Math.floor(currentTime % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  return (
    <div className={styles.container}>
      <Button
        variant="text"
        onClick={handlePlay}
        classes={{ root: styles.playButton }}
      >
        {isPlaying ? <IoPauseOutline className={styles.playIcon} /> : <IoPlayOutline className={styles.playIcon} />}
      </Button>

      <div className={styles.progressBar}>
        <Progress
          currentTime={currentTime}
          duration={duration}
          sections={sections.map(section => section.start)}
          sectionTitles={sections.map(section => section.title)}
          onSeek={handleSeek}
        />
      </div>

      <div className={styles.actions}>
        <Typography variant="p">
          {formatCurrentTime()}
        </Typography>

        <Button variant="text" onClick={handleSkipSection}>
          <SkipSection />
        </Button>

        <VolumeControl volume={volume} onChange={setVolume} />
      </div>

      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        className={styles.audioElement}
      />
    </div>
  )
}

export default AudioTracker;
